import { FileObject } from '@citruscamps/citrus-client'
import { faArrowToTop } from '@fortawesome/pro-regular-svg-icons'
import { faArrowToLeft, faTimes, faUndo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useRef, useState } from 'react'
import ReactCrop, { Crop, convertToPixelCrop } from 'react-image-crop'
import { useQuery } from 'react-query'
import { generateBaseKey } from '../../utils/key-generator'
import { LoadingButton } from '../LoadingButton/LoadingButton'
import { centerAspectCrop } from './utils/centerAspectCrop'
import { cropImage } from './utils/cropImage'
import { fileToDataURL } from './utils/fileToDataURL'
import { modifyImage } from './utils/modifyImage'

interface ISettings {
  rotateDegrees: number
  flipX: boolean
  flipY: boolean
}

export interface IProps {
  file: Pick<FileObject, 'url' | 'filetype' | 'filename'>
  circularCrop?: boolean
  hidden: boolean
  baseHeight?: number
  initialCrop?: Crop
  aspect?: number
  onCancel?: () => void | Promise<void>
  onChange?: (value: string) => void | Promise<void>
  onReset?: () => void | Promise<void>
  onSubmit?: (file: File) => void | Promise<void>
}

export const CropModal = ({
  circularCrop,
  file,
  baseHeight: height = 500,
  initialCrop,
  aspect = 1,
  hidden: isHidden,
  onCancel: handleCancel,
  onReset: _handleReset,
  onSubmit: handleSubmit,
}: IProps): React.ReactElement | null => {
  const ref = useRef<HTMLImageElement>(null)
  const shadowRef = useRef<HTMLImageElement>(null)
  let { url = '', filetype, filename } = file
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0,
    ...initialCrop,
  })
  const [settings, setSettings] = useState<ISettings>({
    rotateDegrees: 0,
    flipX: false,
    flipY: false,
  })
  const { rotateDegrees, flipX, flipY } = settings
  filetype = circularCrop ? 'image/png' : filetype
  const { data: imgSrc = url, error } = useQuery<string, Error>(
    generateBaseKey({
      type: 'cropped_image',
      program_id: undefined,
      query: {
        rotateDegrees,
        flipX,
        flipY,
        src: shadowRef.current?.src || '',
      },
    }),
    async () => {
      if (!shadowRef.current) return url
      const image = await modifyImage({
        ref: shadowRef,
        filetype,
        filename,
        rotateDegrees,
        flipX,
        flipY,
      })
      if (!image) return url
      const src = await fileToDataURL(image)
      if (!src) return url
      return src
    },
    { enabled: !!url && !!shadowRef.current, cacheTime: 0 },
  )

  const handleReset = async (): Promise<void> => {
    setSettings((prev) => ({
      ...prev,
      rotateDegrees: 0,
      flipX: false,
      flipY: false,
    }))
    setCrop({
      unit: '%',
      width: 100,
      height: 100,
      x: 0,
      y: 0,
      ...initialCrop,
    })
    _handleReset?.()
  }

  const handleCrop = useCallback(async (): Promise<void> => {
    if (!ref.current) return
    const image: File = await cropImage({
      ref,
      crop: convertToPixelCrop(crop, ref.current.width, ref.current.height),
      filename,
      filetype,
      circularCrop,
    })
    await handleSubmit?.(image)
  }, [crop, filename, filetype, handleSubmit, circularCrop])

  if (isHidden) {
    return null
  }

  return (
    <>
      <div className="modal fade show fade-in overlay d-flex justify-content-center">
        <div
          className="slide-in-blurred-top col-12 col-sm-10 col-lg-8 mx-0 my-auto"
          style={{ maxWidth: 'unset' }}
        >
          <div className="modal-content">
            <div className="modal-header border-0">
              <button type="button" className="btn text-body ms-auto" onClick={handleCancel}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <div className="row mb-3 mt-2">
                <div className="col-lg-8">
                  <div className="d-flex p-lg-3">
                    {!!imgSrc && (
                      <ReactCrop
                        key={ref.current?.src || ''}
                        crop={crop}
                        onChange={(newCrop: Crop): void => setCrop(newCrop)}
                        keepSelection={true}
                        ruleOfThirds={true}
                        circularCrop={circularCrop}
                        maxWidth={height * aspect}
                        maxHeight={height}
                        minWidth={100}
                        minHeight={100}
                        aspect={aspect}
                      >
                        <img
                          ref={ref}
                          alt="Crop me"
                          src={imgSrc}
                          crossOrigin="anonymous"
                          onLoad={(e) => {
                            if (aspect) {
                              const { width, height } = e.currentTarget
                              setCrop(centerAspectCrop(width, height, aspect))
                            }
                          }}
                        />
                      </ReactCrop>
                    )}
                  </div>
                  <img
                    id="img-shadow"
                    ref={shadowRef}
                    alt="Hidden crop"
                    src={url}
                    className="position-absolute w-100 p-3 visually-hidden"
                    style={{
                      maxWidth: height * aspect,
                      maxHeight: height,
                      minWidth: 100,
                      minHeight: 100,
                      zIndex: -1,
                    }}
                    crossOrigin="anonymous"
                  />
                  <div className="invalid-feedback d-block">{error?.message || '\u00A0'}</div>
                </div>
                <div className="col-lg-4 d-flex text-center">
                  <div className="m-auto">
                    <div>
                      <button
                        type="button"
                        className="btn btn-link m-1"
                        onClick={() =>
                          setSettings((prev) => ({ ...prev, rotateDegrees: 0, flipX: !prev.flipX }))
                        }
                      >
                        <div className="fa-layers me-2">
                          <FontAwesomeIcon
                            icon={faArrowToLeft}
                            transform="flip-h shrink-4 left-4"
                          />
                          <FontAwesomeIcon icon={faArrowToLeft} transform="shrink-4 right-4" />
                        </div>
                        Flip X
                      </button>
                      <button
                        type="button"
                        className="btn btn-link m-1"
                        onClick={() =>
                          setSettings((prev) => ({ ...prev, rotateDegrees: 0, flipY: !prev.flipY }))
                        }
                      >
                        <div className="fa-layers me-2">
                          <FontAwesomeIcon icon={faArrowToTop} transform="flip-v shrink-4 up-4" />
                          <FontAwesomeIcon icon={faArrowToTop} transform=" shrink-4 down-4" />
                        </div>
                        Flip Y
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-link m-1"
                        onClick={() =>
                          setSettings((prev) => ({
                            flipX: false,
                            flipY: false,
                            rotateDegrees: prev.rotateDegrees - 90,
                          }))
                        }
                      >
                        <FontAwesomeIcon className="me-2" icon={faUndo} />
                        -90°
                      </button>
                      <button
                        type="button"
                        className="btn btn-link m-1"
                        onClick={() =>
                          setSettings((prev) => ({
                            flipX: false,
                            flipY: false,
                            rotateDegrees: prev.rotateDegrees + 90,
                          }))
                        }
                      >
                        <FontAwesomeIcon className="me-2" icon={faUndo} transform="flip-h" />
                        90°
                      </button>
                    </div>
                    {!!handleReset && (
                      <div>
                        <button
                          type="button"
                          className="btn btn-link m-1"
                          onClick={() => handleReset()}
                        >
                          Reset Image
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between border-0">
              <button type="button" className="btn btn-link me-auto" onClick={handleCancel}>
                Cancel
              </button>
              <LoadingButton type="button" className="btn btn-primary" onClick={handleCrop}>
                Crop Image
              </LoadingButton>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}
